import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { MenuItem, Select, Typography } from '@mui/material';

import { FormattedValue } from '../store/formatted-value';
import { Group } from '../organisms/group';
import { ValueBox } from '../molecules/value-box';
import { ChartBox } from '../molecules/chart-box';
import { ControlBox } from '../atoms/control-box';
import { useSelector } from '../store/utils';
import { ElectricalGrid } from '../atoms/icons/electrical-grid';

const EnergyMetersPage = () => {
  const metersConfig = useSelector((s) => s.variables.EM_METERS_CONFIG);

  const meterIds = useMemo(() => Object.keys(metersConfig), [metersConfig]);
  const [selectedMeterId, setSelectedMeterId] = useState(meterIds[0] || '');

  // Auto-select first meter if current selection is invalid
  React.useEffect(() => {
    if (meterIds.length > 0 && !meterIds.includes(selectedMeterId)) {
      setSelectedMeterId(meterIds[0]);
    }
  }, [meterIds, selectedMeterId]);

  if (meterIds.length === 0) {
    return (
      <>
        <Group layout="list">
          <Typography variant="body1" sx={{ p: 2 }}>
            <FormattedMessage
              id="energyMeters.notConfigured"
              defaultMessage="Elektroměry nejsou nakonfigurovány. Pro nastavení přejděte do servisního menu."
            />
          </Typography>
        </Group>
      </>
    );
  }

  return (
    <>
      <Group layout="list">
        {meterIds.length > 1 && (
          <ControlBox
            title={
              <FormattedMessage
                id="energyMeters.meter"
                defaultMessage="Elektroměr"
              />
            }
            fullWidth
            control={
              <Select
                value={selectedMeterId}
                onChange={(e) => setSelectedMeterId(e.target.value as string)}
              >
                {meterIds.map((meterId) => (
                  <MenuItem value={meterId} key={meterId}>
                    {metersConfig[meterId]?.name || meterId}
                  </MenuItem>
                ))}
              </Select>
            }
          />
        )}
      </Group>

      <Group
        title={
          <FormattedMessage
            id="energyMeters.statistics"
            defaultMessage="Statistiky"
          />
        }
        layout="list"
      >
        <ChartBox
          variableId="EM_METER_POWER_GRAPH"
          variableSuffix={selectedMeterId}
        />
      </Group>

      <Group
        title={
          <FormattedMessage
            id="energyMeters.general"
            defaultMessage="Obecné informace"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="energyMeters.actualPower"
              defaultMessage="Aktuální výkon"
            />
          }
          value={
            <FormattedValue
              variableId={`EM_METERS_DATA["${selectedMeterId}"].actualPower`}
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }}
            />
          }
          icon={<ElectricalGrid />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="energyMeters.phase1"
                defaultMessage="Fáze 1:"
              />
              &nbsp;
              <FormattedValue
                variableId={`EM_METERS_DATA["${selectedMeterId}"].phase1Power`}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
              <br />
              <FormattedMessage
                id="energyMeters.phase2"
                defaultMessage="Fáze 2:"
              />
              &nbsp;
              <FormattedValue
                variableId={`EM_METERS_DATA["${selectedMeterId}"].phase2Power`}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
              <br />
              <FormattedMessage
                id="energyMeters.phase3"
                defaultMessage="Fáze 3:"
              />
              &nbsp;
              <FormattedValue
                variableId={`EM_METERS_DATA["${selectedMeterId}"].phase3Power`}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
            </Typography>
          }
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="energyMeters.totalEnergy"
              defaultMessage="Celková spotřeba"
            />
          }
          value={
            <FormattedValue
              variableId={`STATS_EM_METERS_ENERGY_TOTAL["${selectedMeterId}"]`}
              fallbackValue={0}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<ElectricalGrid />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="energyMeters.todayEnergy"
                defaultMessage="Dnešní spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId={`STATS_EM_METERS_ENERGY_TODAY["${selectedMeterId}"]`}
                fallbackValue={0}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="energyMeters.yearEnergy"
                defaultMessage="Roční spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId={`STATS_EM_METERS_ENERGY_YEAR["${selectedMeterId}"]`}
                fallbackValue={0}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            </Typography>
          }
        />
      </Group>
    </>
  );
};

export default EnergyMetersPage;
